import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LinkRow } from '../components/LinkRow';
import { Button } from '../design/Button';
import { ChevronBand, Label, Paper, Rule } from '../design/Paper';
import { ro } from '../i18n/ro';
import { createRoom, type CreatedRoom } from '../state/api';
import { joinLinkFor, projectorLinkFor, setAdminToken } from '../state/storage';

/**
 * Room creation. The admin decides once whether they play, then gets the two
 * links they need to hand out: one for players, one for the shared screen.
 *
 * There is no admin link. Admin rights live only in this browser's storage —
 * no URL confers them, so nothing shown here is dangerous to share, and a room
 * lives and dies with the browser that created it.
 */
export function CreateRoomScreen() {
  const navigate = useNavigate();
  const [created, setCreated] = useState<CreatedRoom | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create(adminIsPlaying: boolean) {
    setBusy(true);
    setError(null);
    try {
      const room = await createRoom({ adminIsPlaying });
      setAdminToken(room.roomToken, room.adminToken);
      setCreated(room);
    } catch {
      setError(ro.errors.INVALID_PAYLOAD);
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    const origin = window.location.origin;
    return (
      <Paper className="flex min-h-full flex-col">
        <Header />
        <main className="flex flex-1 flex-col gap-6 px-5 py-7">
          <div>
            <h1 className="font-display text-2xl uppercase leading-tight">{ro.roomReady}</h1>
            <p className="mt-2 text-sm text-ink-soft">{ro.roomReadyBody}</p>
          </div>

          <LinkRow
            title={ro.joinLinkTitle}
            body={ro.joinLinkBody}
            url={joinLinkFor(created.roomToken, origin)}
            testId="join-link"
          />

          <LinkRow
            title={ro.projectorLinkTitle}
            body={ro.projectorLinkBody}
            url={projectorLinkFor(created.roomToken, origin)}
            testId="projector-link"
          />

          <div className="mt-auto pt-4">
            <Button
              tone="ink"
              size="lg"
              block
              data-testid="enter-room"
              onClick={() => navigate(`/r/${created.roomToken}`)}
            >
              {ro.enterRoom}
            </Button>
          </div>
        </main>
      </Paper>
    );
  }

  return (
    <Paper className="flex min-h-full flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-6 px-5 py-8">
        <div>
          <h1 className="font-display text-3xl uppercase leading-tight">{ro.createRoom}</h1>
          <p className="mt-2 text-sm text-ink-soft">
            Alegerea se face o singură dată și rămâne pentru toată camera.
          </p>
        </div>

        <Rule />

        <div className="flex flex-col gap-3">
          <Button
            tone="ink"
            size="lg"
            block
            disabled={busy}
            data-testid="create-playing"
            onClick={() => void create(true)}
          >
            {ro.adminIsPlaying}
          </Button>
          <Button
            tone="paper"
            size="lg"
            block
            disabled={busy}
            data-testid="create-not-playing"
            onClick={() => void create(false)}
          >
            {ro.adminNotPlaying}
          </Button>
        </div>

        {error && <p className="text-sm text-red">{error}</p>}
      </main>
    </Paper>
  );
}

function Header() {
  return (
    <>
      <header className="relative shrink-0 bg-ink px-5 py-4 text-paper">
        <Label className="text-xs">{ro.appName}</Label>
        <div className="mt-1 text-[0.6rem] uppercase tracking-[0.14em] opacity-60">
          {ro.appSub}
        </div>
      </header>
      <ChevronBand className="shrink-0 text-ink" />
    </>
  );
}
